// /app/generations/$generationId — generation result with multi-variant
// accept/reject UX.
//
// v0.2.0 flow:
//   1. The generation produces N versions per platform (N = providers ×
//      variants_per_platform). All start in status='pending'.
//   2. User reviews variants side-by-side per platform. Clicking Accept
//      flips one to 'accepted' and auto-rejects pending siblings. Reject
//      flips just that one.
//   3. Once at least one variant per platform is accepted, the bulk
//      "Approve & create drafts" button promotes the accepted versions to
//      content.posts in draft state. The existing approve endpoint takes
//      explicit selections so we pass the accepted version IDs.

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { StatusPill, generationStatusTone } from "../../../components/StatusPill";
import { PostPreview } from "../../../components/PostPreview";
import { ScheduleModal } from "../../../components/content/ScheduleModal";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  assetURL,
  brands as brandsAPI,
  generations,
  type Brand,
  type BrandAsset,
  type GenerationResponse,
  type GenerationVersion,
  type Platform,
} from "../../../lib/api";

function GenerationDetail() {
  const { t } = useI18n();
  const { generationId } = Route.useParams();
  const [data, setData] = useState<GenerationResponse | null>(null);
  const [loadErr, setLoadErr] = useState<"not_found" | "error" | null>(null);
  const [aiDisclosure, setAiDisclosure] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveErr, setApproveErr] = useState<string | null>(null);
  // Local optimistic state for accept/reject so the UI feels instant.
  const [pendingAction, setPendingAction] = useState<string | null>(null); // version_id
  // v0.2.49 — tabbed variant review: only one platform's variants
  // render at a time, with a tab strip up top showing per-platform
  // status. Default = the first target platform.
  const [activeTab, setActiveTab] = useState<string | null>(null);
  // v0.2.49 — schedule modal shown after the operator hits the
  // primary "Schedule N posts" button.
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    generations
      .get(generationId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e instanceof ApiError && e.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [generationId]);

  // Brand fetch — needed so PostPreview can render the brand name +
  // avatar (otherwise variants show "?" and look unbranded).
  const [brand, setBrand] = useState<Brand | null>(null);
  useEffect(() => {
    if (!data?.generation.brand_id) return;
    let cancelled = false;
    brandsAPI.get(data.generation.brand_id)
      .then((b) => { if (!cancelled) setBrand(b); })
      .catch(() => { /* non-fatal — preview falls back to brand-id */ });
    return () => { cancelled = true; };
  }, [data?.generation.brand_id]);

  // Group versions by platform, preserving server order so v1 comes before v2.
  const byPlatform = useMemo(() => {
    const m = new Map<string, GenerationVersion[]>();
    if (!data) return m;
    for (const v of data.versions) {
      const arr = m.get(v.platform) ?? [];
      arr.push(v);
      m.set(v.platform, arr);
    }
    return m;
  }, [data]);

  async function accept(versionId: string) {
    setPendingAction(versionId);
    try {
      await generations.acceptVersion(versionId);
      const fresh = await generations.get(generationId);
      setData(fresh);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setPendingAction(null);
    }
  }

  async function reject(versionId: string) {
    setPendingAction(versionId);
    try {
      await generations.rejectVersion(versionId);
      const fresh = await generations.get(generationId);
      setData(fresh);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setPendingAction(null);
    }
  }

  // ---- Creative iteration (v0.2.7): edit / replace-image / regenerate ----

  async function saveEdit(versionId: string, text: string, hashtags: string[]) {
    setPendingAction(versionId);
    setApproveErr(null);
    try {
      await generations.editVersion(versionId, { text_body: text, hashtags });
      const fresh = await generations.get(generationId);
      setData(fresh);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setPendingAction(null);
    }
  }

  async function regenerate(platform: Platform, notes: string) {
    setApproveErr(null);
    setPendingAction("regen:" + platform);
    try {
      // Match scope to what the original generation produced. If the
      // user asked for text+image, the retry should also be text+image
      // — previously we hardcoded "text" which meant no image ever got
      // regenerated on retry. Falls back to "text" for text-only gens.
      const types = data?.generation?.content_types ?? ["text"];
      const wantText = types.includes("text");
      const wantImage = types.includes("image");
      const scope: "text" | "image" | "both" =
        wantText && wantImage ? "both" : wantImage ? "image" : "text";
      await generations.regenerate(generationId, { platform, notes, scope });
      const fresh = await generations.get(generationId);
      setData(fresh);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setPendingAction(null);
    }
  }

  async function replaceImage(versionId: string, assetId: string) {
    setPendingAction(versionId);
    setApproveErr(null);
    try {
      await generations.replaceVersionImage(versionId, assetId);
      const fresh = await generations.get(generationId);
      setData(fresh);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setPendingAction(null);
    }
  }

  // Build selections from accepted versions — one per platform. If a
  // platform has zero accepted versions, the bulk-approve button stays
  // disabled and we hint at it.
  const acceptedByPlatform = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [pf, vs] of byPlatform) {
      const acc = vs.find((v) => v.status === "accepted");
      if (acc) m[pf] = acc.id;
    }
    return m;
  }, [byPlatform]);

  // Partial approve allowed: as long as AT LEAST ONE platform has an
  // accepted variant, the user can finalize. Platforms without an
  // accepted variant are skipped — the backend approve handler only
  // creates posts for selections in the map. v0.4.x — operator
  // feedback: "accepted 3 of 4, can't approve, broken cycle".
  const acceptedPlatformCount = Object.keys(acceptedByPlatform).length;
  const totalPlatformCount = data?.generation.target_platforms.length ?? 0;
  const canApprove = acceptedPlatformCount > 0;
  const missingPlatforms = data?.generation.target_platforms.filter(
    (p) => !acceptedByPlatform[p],
  ) ?? [];

  // v0.2.49 — approve flow now goes through the ScheduleModal.
  // openSchedule() shows the modal; submitWithSchedule(when) is the
  // modal's onConfirm callback. The legacy direct approve is preserved
  // here as approveDirectly() for callers that don't need the modal
  // (e.g. the bulk approve path on the bulk-job detail page).
  async function submitWithSchedule(when: { scheduledFor?: string; saveAsDraft?: boolean }) {
    if (!data || !canApprove) return;
    setApproving(true);
    setApproveErr(null);
    try {
      await generations.approve(generationId, {
        selections: acceptedByPlatform as Record<Platform, string>,
        ai_disclosure: aiDisclosure,
        scheduled_for: when.scheduledFor,
        save_as_draft: when.saveAsDraft,
      });
      const fresh = await generations.get(generationId);
      setData(fresh);
      setScheduleOpen(false);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setApproving(false);
    }
  }

  if (loadErr === "not_found") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <span className="eyebrow">404</span>
          <h2 className="font-display text-xl font-semibold mt-2">{t("notfound.title")}</h2>
          <Link to="/app/content" className="text-secondary hover:text-primary text-sm mt-4 inline-block">
            ← {t("content.title")}
          </Link>
        </Card>
      </div>
    );
  }
  if (loadErr || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          {loadErr ? <p className="text-error">{t("common.errorGeneric")}</p>
            : <p className="text-secondary">{t("common.loading")}</p>}
        </Card>
      </div>
    );
  }

  const g = data.generation;
  const platforms = g.target_platforms;
  const totalCost = data.versions.reduce((s, v) => s + v.cost_cents, 0);
  const brandName = brand?.trade_name || brand?.legal_name || "—";

  // v0.2.49 — activeTab defaults to first target platform; snaps if
  // the current activeTab gets dropped (shouldn't happen often but
  // covers post-rejection state changes).
  if (activeTab === null && platforms.length > 0) {
    // Avoid setState during render — schedule via effect-like pattern.
    // This runs once per mount; setTimeout(0) defers to next tick.
    setTimeout(() => setActiveTab(platforms[0] ?? null), 0);
  }
  const currentTab = activeTab && platforms.includes(activeTab as Platform) ? activeTab : platforms[0];

  // Progress metric — "X of Y platforms accepted". Drives the
  // status-strip + the sticky-bottom approve button visibility.
  const acceptedPlatforms = platforms.filter((p) =>
    (byPlatform.get(p) ?? []).some((v) => v.status === "accepted"),
  );
  const progressPct = platforms.length === 0
    ? 0
    : Math.round((acceptedPlatforms.length / platforms.length) * 100);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6 pb-[120px]">
      {/* ────────────────── HERO ────────────────── */}
      <div className="flex flex-col gap-4">
        <Link
          to="/app/content/$brandId"
          params={{ brandId: g.brand_id }}
          className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block w-fit"
        >
          ← {t("content.title")}
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-semibold">{t("gen.detail.title")}</h1>
              <StatusPill tone={generationStatusTone(g.status)}>{t(`content.gen.status.${g.status}`)}</StatusPill>
            </div>
            <p className="text-xs text-muted font-mono">
              {brandName} · {new Date(g.created_at).toLocaleString()} · {(totalCost / 100).toFixed(2)}¢ · {g.target_platforms.length} {t("gen.detail.platformsLabel")}
            </p>
          </div>

          {/* Progress ring */}
          {g.status === "ready" || g.status === "approved" ? (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-muted font-mono">{t("gen.detail.progressLabel")}</div>
                <div className="text-sm font-semibold text-primary">
                  {acceptedPlatforms.length} / {platforms.length} {t("gen.detail.platformsLabel")}
                </div>
              </div>
              <div className="w-12 h-12 relative flex items-center justify-center">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90" aria-hidden>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="var(--color-elevated)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="14" fill="none" stroke="var(--color-red)" strokeWidth="3"
                    strokeDasharray={`${(progressPct / 100) * (2 * Math.PI * 14)} ${2 * Math.PI * 14}`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute text-[10px] font-mono text-primary">{progressPct}%</span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Prompt — collapsed by default, expandable */}
        <Card className="!p-4">
          <details>
            <summary className="cursor-pointer list-none flex items-center gap-2 ring-nexus rounded">
              <span className="text-[10px] uppercase tracking-wider font-mono text-muted">{t("gen.detail.prompt")}</span>
              <span className="text-sm text-secondary line-clamp-1 flex-1">{g.prompt}</span>
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-muted" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </summary>
            <p className="text-primary whitespace-pre-wrap text-sm mt-3 pt-3 border-t border-border-default">{g.prompt}</p>
          </details>
        </Card>
      </div>

      {/* ────────────────── PER-PLATFORM (tabbed v0.2.49) ────────────────── */}
      {/* Tab strip — one tab per target platform with a status dot:
            ● accepted (green)  ● rejected (red)  ● pending (grey) */}
      <div className="flex items-center gap-1 border-b border-border-default overflow-x-auto -mx-1 px-1">
        {platforms.map((pf) => {
          const vs = byPlatform.get(pf) ?? [];
          const acceptedHere = vs.some((v) => v.status === "accepted");
          const rejectedAll = vs.length > 0 && vs.every((v) => v.status === "rejected");
          const isActive = pf === currentTab;
          const dot = acceptedHere ? "bg-success" : rejectedAll ? "bg-error" : "bg-muted";
          return (
            <button
              key={pf}
              type="button"
              onClick={() => setActiveTab(pf)}
              aria-pressed={isActive}
              className={[
                "ring-nexus inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition -mb-px whitespace-nowrap",
                isActive
                  ? "border-red text-primary"
                  : "border-transparent text-secondary hover:text-primary",
              ].join(" ")}
            >
              <PlatformIcon platform={pf} />
              <span className="capitalize">{pf}</span>
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />
              <span className="text-[10px] font-mono text-muted">({vs.length})</span>
            </button>
          );
        })}
      </div>

      {/* Active tab's variants only — no more stacked 16-card scroll */}
      {currentTab ? (() => {
        const pf = currentTab;
        const vs = byPlatform.get(pf) ?? [];
        const acceptedHere = vs.find((v) => v.status === "accepted");
        return (
          <section key={pf} className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2.5">
                <span className="text-[11px] font-mono text-muted">
                  {vs.length} {vs.length === 1 ? t("gen.detail.variantSingular") : t("gen.detail.variantPlural")}
                </span>
              </div>
              {acceptedHere ? (
                <span className="text-[11px] font-mono text-success inline-flex items-center gap-1">
                  ✓ {t("gen.detail.variantAcceptedHere")}
                </span>
              ) : (
                <span className="text-[11px] font-mono text-muted">{t("gen.detail.variantPickHint")}</span>
              )}
            </div>

            {vs.length === 0 ? (
              <Card className="!p-4"><p className="text-secondary text-sm">—</p></Card>
            ) : (
              <div className={vs.length > 1 ? "grid md:grid-cols-2 gap-4" : "flex flex-col gap-4"}>
                {vs.map((v) => (
                  <VariantCard
                    key={v.id}
                    v={v}
                    busy={pendingAction === v.id}
                    onAccept={() => void accept(v.id)}
                    onReject={() => void reject(v.id)}
                    onSaveEdit={(text, hashtags) => void saveEdit(v.id, text, hashtags)}
                    onReplaceImage={(assetId) => void replaceImage(v.id, assetId)}
                    locked={g.status === "approved"}
                    brandId={g.brand_id}
                    brandName={brandName}
                  />
                ))}
              </div>
            )}

            {g.status !== "approved" ? (
              <RegenerateRow
                platform={pf}
                busy={pendingAction === "regen:" + pf}
                onSubmit={(notes) => void regenerate(pf as Platform, notes)}
              />
            ) : null}
          </section>
        );
      })() : null}

      {/* ────────────────── STATUS / APPROVED ────────────────── */}
      {g.status === "approved" ? (
        <Card>
          <p className="text-info text-sm">{t("gen.detail.approvedNote")}</p>
          <Link to="/app/content/$brandId" params={{ brandId: g.brand_id }} className="mt-3 inline-block">
            <Button variant="secondary">{t("content.title")} →</Button>
          </Link>
        </Card>
      ) : g.status === "running" || g.status === "pending" ? (
        <Card><p className="text-warning text-sm">{t("gen.create.generating")}</p></Card>
      ) : null}

      {/* ────────────────── STICKY APPROVE BAR ────────────────── */}
      {g.status === "ready" ? (
        <div className="fixed inset-x-0 bottom-0 z-20 bg-shell/95 backdrop-blur border-t border-border-default px-4 sm:px-6 py-3">
          <div className="mx-auto max-w-6xl flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="flex items-center gap-3 text-xs text-secondary">
                <span className="font-mono">{acceptedPlatformCount}/{totalPlatformCount} {t("gen.detail.platformsLabel")}</span>
                {acceptedPlatformCount === 0 ? (
                  <span className="text-muted hidden sm:inline">{t("gen.detail.needAccepted")}</span>
                ) : acceptedPlatformCount < totalPlatformCount ? (
                  <span className="text-warning hidden sm:inline">
                    {t("gen.detail.partialApproveHint").replace("{platforms}", missingPlatforms.join(", "))}
                  </span>
                ) : (
                  <span className="text-success hidden sm:inline">{t("gen.detail.readyToApprove")}</span>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-muted">
                <input
                  type="checkbox"
                  checked={aiDisclosure}
                  onChange={(e) => setAiDisclosure(e.target.checked)}
                  className="ring-nexus accent-red w-3.5 h-3.5"
                />
                {t("post.detail.aiDisclosure")}
              </label>
            </div>
            <div className="flex items-center gap-3">
              {approveErr ? <p className="text-error text-xs" role="alert">{approveErr}</p> : null}
              {/* v0.2.49 — primary action is now "Schedule N posts"
                  (opens ScheduleModal). Draft becomes an opt-in
                  inside the modal, not the default. */}
              <Button
                variant="primary"
                onClick={() => setScheduleOpen(true)}
                loading={approving}
                disabled={!canApprove}
              >
                {t("schedule.openCta").replace("{n}", String(acceptedPlatformCount))}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* v0.2.49 — schedule modal. Opens when operator clicks the
          primary "Schedule N posts" button. Approves with the chosen
          timestamp (or save_as_draft for the rare escape hatch). */}
      {scheduleOpen ? (
        <ScheduleModal
          count={acceptedPlatformCount}
          busy={approving}
          onConfirm={(when) => void submitWithSchedule(when)}
          onCancel={() => setScheduleOpen(false)}
        />
      ) : null}
    </div>
  );
}

function VariantCard({
  v, busy, onAccept, onReject, onSaveEdit, onReplaceImage, locked, brandId, brandName,
}: {
  v: GenerationVersion;
  busy: boolean;
  onAccept: () => void;
  onReject: () => void;
  onSaveEdit: (text: string, hashtags: string[]) => void;
  onReplaceImage: (assetId: string) => void;
  locked: boolean;
  brandId: string;
  brandName: string;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<"view" | "edit-text" | "pick-image">("view");

  // Wrapper tint — accepted = subtle red-tinted glow (brand-aligned),
  // rejected = grayscaled + faded, pending = neutral.
  const wrapTone =
    v.status === "accepted"
      ? "border-red ring-2 ring-red-muted"
      : v.status === "rejected"
        ? "border-border-default opacity-40 grayscale"
        : "border-border-default hover:border-border-strong";
  const editable = v.status === "pending" && !locked;

  // Compose the preview text — text body + optional hashtags so the
  // mockup shows what the post will look like fully assembled.
  const previewText = useMemo(() => {
    const parts: string[] = [];
    if (v.text_body) parts.push(v.text_body);
    if (v.hashtags && v.hashtags.length > 0) {
      parts.push(v.hashtags.map((h) => `#${h}`).join(" "));
    }
    return parts.join("\n\n");
  }, [v.text_body, v.hashtags]);

  return (
    <Card className={`!p-0 overflow-hidden border transition-all ${wrapTone}`}>
      {/* Header strip — variant id + provider + status */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border-default bg-elevated/30">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-secondary">v{v.version_number}</span>
          <span className="rounded-full border border-border-default px-2 py-0.5 text-[10px] font-mono text-muted capitalize">
            {v.provider}
          </span>
        </div>
        <VariantStatusPill status={v.status} />
      </div>

      {/* Body — preview OR an edit form */}
      <div className="px-4 py-4">
        {mode === "edit-text" && editable ? (
          <EditTextForm
            initialText={v.text_body ?? ""}
            initialHashtags={v.hashtags ?? []}
            busy={busy}
            onCancel={() => setMode("view")}
            onSave={(text, tags) => { onSaveEdit(text, tags); setMode("view"); }}
          />
        ) : mode === "pick-image" && editable ? (
          <ImagePicker
            brandId={brandId}
            busy={busy}
            currentAssetId={v.image_asset_id ?? null}
            onCancel={() => setMode("view")}
            onPick={(assetId) => { onReplaceImage(assetId); setMode("view"); }}
          />
        ) : (
          // PostPreview renders the variant as it'll actually look on
          // the platform — replaces the old raw-text-with-image block.
          <div className="flex justify-center">
            <PostPreview
              platform={v.platform}
              brandName={brandName}
              text={previewText}
              imageURL={v.image_url ?? undefined}
            />
          </div>
        )}
      </div>

      {/* Debug expander — operator-visible reveal of what was actually
          sent to the model. Critical for diagnosing "why is the image
          generic?" / "why isn't brand voice present?" */}
      <PromptDebugExpander promptUsed={v.prompt_used ?? null} />

      {/* Footer — actions + meta. Big Accept button on the right. */}
      <div className="px-4 py-3 border-t border-border-default bg-elevated/30 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono text-muted flex flex-wrap items-center gap-1.5">
          <span>{v.text_model ?? "—"}</span>
          <span>·</span>
          <span>{(v.cost_cents / 100).toFixed(2)}¢</span>
          {v.latency_ms ? <><span>·</span><span>{v.latency_ms}ms</span></> : null}
        </div>
        {!locked && mode === "view" ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {editable ? (
              <>
                <button
                  type="button"
                  onClick={() => setMode("edit-text")}
                  disabled={busy}
                  title={t("gen.detail.editText")}
                  aria-label={t("gen.detail.editText")}
                  className="w-8 h-8 rounded-[8px] text-secondary hover:text-primary hover:bg-elevated ring-nexus transition-colors"
                >
                  <EditIcon />
                </button>
                <button
                  type="button"
                  onClick={() => setMode("pick-image")}
                  disabled={busy}
                  title={t("gen.detail.replaceImage")}
                  aria-label={t("gen.detail.replaceImage")}
                  className="w-8 h-8 rounded-[8px] text-secondary hover:text-primary hover:bg-elevated ring-nexus transition-colors inline-flex items-center justify-center"
                >
                  <ImageIcon />
                </button>
              </>
            ) : null}
            <Button
              variant="ghost"
              type="button"
              onClick={onReject}
              // v0.4.x fix: once a variant is accepted, the reject
              // button MUST be disabled too — otherwise the operator
              // can click reject after accept which leaves the variant
              // in 'rejected' state but the parent "Approve all" panel
              // still thinks this platform is accepted (it cached the
              // earlier accept). Same logic for the other direction:
              // disable accept after reject.
              disabled={busy || v.status !== "pending"}
              className="!text-error hover:!bg-error/10"
            >
              ✕ {t("gen.detail.reject")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={onAccept}
              disabled={busy || v.status !== "pending"}
              loading={busy}
            >
              ✓ {t("gen.detail.accept")}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// --- Debug expander — shows the actual prompt sent to the model ---

function PromptDebugExpander({ promptUsed }: { promptUsed: string | null }) {
  const { t } = useI18n();
  if (!promptUsed) return null;

  // Parse the JSON blob (content-service v0.2.18+). Older versions
  // stored the raw user prompt as a plain string; we render that
  // verbatim with a "(raw)" label so the operator can still inspect it.
  let parsed: { text_system?: string; text_user?: string; image?: string } | null = null;
  try {
    const j = JSON.parse(promptUsed);
    if (j && typeof j === "object") parsed = j;
  } catch {
    /* not JSON — old shape */
  }

  async function copy(label: string, value: string) {
    try { await navigator.clipboard.writeText(value); } catch { /* ignore */ }
    // Tiny visual feedback would be nice; keeping it minimal for now.
    void label;
  }

  return (
    <details className="border-t border-border-default group">
      <summary className="cursor-pointer list-none px-4 py-2 text-[11px] font-mono text-muted hover:text-secondary hover:bg-elevated/40 transition-colors flex items-center gap-2 ring-nexus">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        🛠 {t("gen.detail.debugPrompt")}
      </summary>
      <div className="px-4 py-3 border-t border-border-default bg-canvas/40 flex flex-col gap-3">
        {parsed ? (
          <>
            {parsed.text_system ? (
              <PromptBlock label="text · system" value={parsed.text_system} onCopy={copy} />
            ) : null}
            {parsed.text_user ? (
              <PromptBlock label="text · user" value={parsed.text_user} onCopy={copy} />
            ) : null}
            {parsed.image ? (
              <PromptBlock label="image" value={parsed.image} onCopy={copy} />
            ) : null}
          </>
        ) : (
          <PromptBlock label="raw" value={promptUsed} onCopy={copy} />
        )}
      </div>
    </details>
  );
}

function PromptBlock({ label, value, onCopy }: {
  label: string;
  value: string;
  onCopy: (label: string, value: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.16em] font-mono text-red">{label}</span>
        <button
          type="button"
          onClick={() => onCopy(label, value)}
          className="text-[10px] font-mono text-muted hover:text-primary ring-nexus rounded px-1.5 py-0.5"
        >
          copy
        </button>
      </div>
      <pre className="text-[11px] whitespace-pre-wrap break-words bg-shell border border-border-default rounded-[8px] p-2.5 text-secondary leading-relaxed max-h-[200px] overflow-y-auto">
        {value}
      </pre>
    </div>
  );
}

// --- Inline icons ---

function PlatformIcon({ platform }: { platform: string }) {
  const common = "w-4 h-4";
  switch (platform) {
    case "instagram":
      return (
        <svg viewBox="0 0 24 24" className={common} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <rect x="3" y="3" width="18" height="18" rx="5"/>
          <circle cx="12" cy="12" r="3.5"/>
          <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none"/>
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" className={common} fill="currentColor" aria-hidden>
          <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.77l-.44 2.9h-2.33v6.99A10 10 0 0 0 22 12Z"/>
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" className={common} fill="currentColor" aria-hidden>
          <path d="M4.98 3.5a2.5 2.5 0 1 1-.01 5.01A2.5 2.5 0 0 1 4.98 3.5ZM3 9h4v12H3V9Zm6 0h3.84v1.71h.06c.53-.95 1.84-1.95 3.79-1.95C20.6 8.76 22 11 22 14.16V21h-4v-6.13c0-1.46-.03-3.34-2.04-3.34-2.04 0-2.36 1.59-2.36 3.23V21h-4V9Z"/>
        </svg>
      );
    case "telegram":
      return (
        <svg viewBox="0 0 24 24" className={common} fill="currentColor" aria-hidden>
          <path d="M9.04 15.6 8.86 19c.41 0 .59-.18.8-.4l1.92-1.82 3.99 2.91c.73.4 1.25.19 1.45-.67l2.62-12.27c.24-1.09-.39-1.51-1.1-1.25L3.21 9.95c-1.06.41-1.04 1.01-.18 1.28l4.18 1.31 9.7-6.11c.46-.3.88-.13.53.18L9.04 15.6Z"/>
        </svg>
      );
  }
  return null;
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 20h9M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function ImageIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <path d="m21 15-5-5L5 21" strokeLinejoin="round"/>
    </svg>
  );
}

// EditTextForm — inline editor for a pending variant's text + hashtags.
// Hashtags input is comma-separated; trimmed and lower-cased on save.
function EditTextForm({
  initialText, initialHashtags, busy, onCancel, onSave,
}: {
  initialText: string;
  initialHashtags: string[];
  busy: boolean;
  onCancel: () => void;
  onSave: (text: string, tags: string[]) => void;
}) {
  const { t } = useI18n();
  const [text, setText] = useState(initialText);
  const [tagsRaw, setTagsRaw] = useState(initialHashtags.join(", "));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const tags = tagsRaw
          .split(/[,\n]/)
          .map((s) => s.trim().replace(/^#/, "").toLowerCase())
          .filter(Boolean);
        onSave(text.trim(), tags);
      }}
      className="flex flex-col gap-2"
      noValidate
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        maxLength={4000}
        className="ring-nexus bg-elevated text-primary border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
      />
      <input
        value={tagsRaw}
        onChange={(e) => setTagsRaw(e.target.value)}
        placeholder={t("gen.detail.hashtagsPlaceholder")}
        className="ring-nexus bg-elevated text-primary border border-border-subtle rounded-lg px-3 py-2 text-sm font-mono"
      />
      <p className="text-xs text-muted">{t("gen.detail.hashtagsHint")}</p>
      <div className="flex justify-end gap-2 mt-1">
        <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
          {t("gen.detail.cancel")}
        </Button>
        <Button variant="primary" type="submit" loading={busy}>
          {t("gen.detail.save")}
        </Button>
      </div>
    </form>
  );
}

// ImagePicker — lists the brand's "example" + "product" assets so the
// user can swap in a hand-picked image. The brand panel handles raw
// uploads (Brand Studio → Examples); this picker is the consumer.
function ImagePicker({
  brandId, busy, currentAssetId, onCancel, onPick,
}: {
  brandId: string;
  busy: boolean;
  currentAssetId: string | null;
  onCancel: () => void;
  onPick: (assetId: string) => void;
}) {
  const { t } = useI18n();
  const [assets, setAssets] = useState<BrandAsset[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    brandsAPI.listAssets(brandId)
      .then((r) => { if (!cancelled) setAssets(r.assets); })
      .catch(() => !cancelled && setErr(t("common.errorGeneric")));
    return () => { cancelled = true; };
  }, [brandId, t]);

  if (err) return <p className="text-error text-sm">{err}</p>;
  if (assets === null) return <p className="text-secondary text-sm">{t("common.loading")}</p>;

  // Filter:
  //  - image MIME only (no documents, no PDF)
  //  - kind=example or kind=product — the operator-uploaded templates.
  //    Excludes 'generated' (AI output cluttering this picker is exactly
  //    what the operator reported as broken UX) and 'logo' (logos
  //    shouldn't replace the post image).
  const imageAssets = assets.filter(
    (a) =>
      a.mime_type.startsWith("image/") &&
      (a.kind === "example" || a.kind === "product"),
  );

  return (
    <div className="flex flex-col gap-3">
      {imageAssets.length === 0 ? (
        <p className="text-secondary text-sm">{t("gen.detail.image.noAssets")}</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {imageAssets.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onPick(a.id)}
              disabled={busy}
              className={`ring-nexus rounded-lg overflow-hidden border ${
                a.id === currentAssetId ? "border-success" : "border-border-default"
              } hover:border-nexus-red transition`}
              title={a.filename_original}
            >
              {/* assetURL() wraps the relative /v1/brands/.../data path
                  with the API base URL. Without it the thumbnail loads
                  as a relative URL against the dashboard origin and
                  404s. Same pattern bit us on the post-detail image
                  render earlier. */}
              <img src={assetURL(a.url)} alt={a.filename_original}
                   className="w-full h-20 object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          type="button"
          onClick={() => onPick("")}
          disabled={busy || !currentAssetId}
          className="!text-error"
        >
          {t("gen.detail.image.clear")}
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
          {t("gen.detail.cancel")}
        </Button>
      </div>
    </div>
  );
}

// RegenerateRow — appears below each platform's variant deck. Opens
// an optional-notes textarea so the user can hint the AI ("make it
// warmer", "less corporate"). Submitting spawns a new pending version.
function RegenerateRow({
  platform, busy, onSubmit,
}: {
  platform: string;
  busy: boolean;
  onSubmit: (notes: string) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="ghost" type="button" onClick={() => setOpen(true)} disabled={busy}>
          🔄 {t("gen.detail.regenerate.cta")}
        </Button>
      </div>
    );
  }
  return (
    <Card className="!p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(notes.trim());
          setOpen(false);
          setNotes("");
        }}
        className="flex flex-col gap-2"
        noValidate
      >
        <label className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
          {t("gen.detail.regenerate.title").replace("{platform}", platform)}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder={t("gen.detail.regenerate.placeholder")}
          className="ring-nexus bg-elevated text-primary border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => { setOpen(false); setNotes(""); }} disabled={busy}>
            {t("gen.detail.cancel")}
          </Button>
          <Button variant="primary" type="submit" loading={busy}>
            {t("gen.detail.regenerate.submit")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function VariantStatusPill({ status }: { status: "pending" | "accepted" | "rejected" }) {
  const { t } = useI18n();
  if (status === "accepted") {
    return <span className="text-[11px] font-mono text-success">✓ {t("gen.detail.statusAccepted")}</span>;
  }
  if (status === "rejected") {
    return <span className="text-[11px] font-mono text-error">✕ {t("gen.detail.statusRejected")}</span>;
  }
  return <span className="text-[11px] font-mono text-muted">{t("gen.detail.statusPending")}</span>;
}

export const Route = createFileRoute("/app/generations/$generationId")({
  component: GenerationDetail,
});
